import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { Button, Checkbox, Chip, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { HoverCard } from '@mantine/core';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { getBolListG2000, updateBolStatus, uploadBolFiles } from '../../api/bolApi';
import { getStores } from '../../api/storeApi';
import BolFilterModal from '../../components/Bol/BolFilterModal';
import BolWorkspace from '../../components/Bol/BolWorkspace';
import AppDialogMessage from '../../components/Common/AppDialogMessage';
import ViewFileModal from '../../components/Common/ViewFileModal';
import GenerateInvoice from '../../components/Invoice/GenerateInvoice';
import BolPrint from '../../components/Print/BolPrint';
import VendorReport from '../../components/Print/VendorReport';
import { useAppDialog } from '../../Hooks/useAppDialog';
import { useDebounce } from '../../Hooks/useDebounce';
import { getDayOfWeek } from '../../Utils/getDaybyDate';

const initialFilter = {
  shipper: '',
  receiver: '',
  po_no: '',
  // bol_no: '',
  type: '',
  invoice_no: '',
  vendor_delivered: null as boolean | null,
  dc_received: null as boolean | null,
  approved: null as boolean | null,
  payment_status: null as boolean | null,
  trailer: '',
  supplier: '',
  start_date: '',
  end_date: '',
  info: '',
};

const BolList: React.FC = () => {
  const [bolList, setBolList] = useState<any[]>([]);
  const [storeList, setStoreList] = useState<any[]>([]);
  const [filterModal, setFilterModal] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 800);
  const { dialog, openDialog, closeDialog } = useAppDialog();
  const [filterDraft, setFilterDraft] = useState(initialFilter);
  const [appliedFilter, setAppliedFilter] = useState(initialFilter);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 1,
    totalRows: 0,
  });
  const [viewFileModal, setViewFileModal] = useState({
    open: false,
    filePath: null as string | null,
    title: 'View File',
  });
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  /**
   * Fetch stores once.
   * Used by VendorReport and any future store-aware rendering.
   */
  useEffect(() => {
    getStores()
      .then((data) => setStoreList(data))
      .catch((error) => console.log(error));
  }, []);

  /**
   * Fetch vendor-side BOL list.
   * Backend is already configured to return vendor-specific records.
   */
  const fetchBolList = async (page: number, limit: number) => {
    try {
      const result = await getBolListG2000({
        page,
        limit,
        bol_no: search.trim(),
        filters: appliedFilter,
      });

      setBolList(result.data || []);

      setPagination((prev) => ({
        ...prev,
        page,
        total: Math.max(1, Math.ceil((result.totalRows || 0) / prev.limit)),
        totalRows: result.totalRows || 0,
      }));
    } catch (error) {
      console.log('Error in getBolListG2000:', error);
    }
  };

  /**
   * Refetch when page changes or applied search/filter changes.
   */
  useEffect(() => {
    fetchBolList(pagination.page, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, appliedFilter]);

  useEffect(() => {
    fetchBolList(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);
  /**
   * Search handler for top bar.
   */
  const handleSearch = (value: string) => {
    setSearch(value);
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  /**
   * Update filter modal draft only.
   */
  const handleFilterChange = (key: string, value: any) => {
    setFilterDraft((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  /**
   * Apply modal draft filters.
   */
  const handleFilterApply = () => {
    setAppliedFilter(filterDraft);
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
    setFilterModal(false);
  };

  /**
   * Reset all active filters and search.
   * This is used from top bar reset and modal reset.
   */
  const handleResetAll = () => {
    setSearch('');
    setFilterDraft(initialFilter);
    setAppliedFilter(initialFilter);
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
    setFilterModal(false);
  };

  /**
   * Update vendor_delivered status.
   */
  const updateStatus = async (checked: boolean, bol_no: string) => {
    try {
      const result = await updateBolStatus({
        bol_no,
        column: 'vendor_delivered',
        status: checked,
      });

      setBolList((prev) =>
        prev.map((bol: any) =>
          bol.bol_no === bol_no
            ? { ...bol, vendor_delivered: result.data?.[0]?.vendor_delivered }
            : bol
        )
      );

      openDialog({
        variant: 'success',
        title: 'Status Updated',
        message: result.message || `${bol_no} updated successfully.`,
        confirmText: 'Close',
        hideCancel: true,
      });
    } catch (error: any) {
      console.log('Error updating status:', error);

      openDialog({
        variant: 'error',
        title: 'Update Failed',
        message: error?.message || 'Something went wrong.',
        confirmText: 'Close',
        hideCancel: true,
      });
    }
  };

  /**
   * Confirm before changing vendor_delivered.
   */
  const handleStatusChange = (checked: boolean, bol_no: string) => {
    const confirm = window.confirm(`Are you sure ${bol_no} is delivered?`);
    if (confirm) {
      updateStatus(checked, bol_no);
    }
  };

  /**
   * Upload signed BOL file.
   * Stored in invoice_file as per existing behavior.
   */
  const handleUploadFile = async (bol_no: string, files: File[]) => {
    try {
      await uploadBolFiles(bol_no, files);

      // Refresh list
      await fetchBolList(pagination.page, pagination.limit);

      openDialog({
        variant: 'success',
        title: 'Upload Successful',
        message: 'File uploaded successfully',
        confirmText: 'Close',
        hideCancel: true,
      });
    } catch (error: any) {
      console.error(error);

      openDialog({
        variant: 'error',
        title: 'Upload Failed',
        message: error?.message || 'Error uploading file',
        confirmText: 'Close',
        hideCancel: true,
      });
    }
  };

  /**
   * Open file modal.
   * Used for viewing signed BOLs and any future file viewing needs.
   * For signed BOLs, we open the modal with the invoice_file path and "View Signed BOL" title.
    */
  const handleOpenFileModal = (filePath: string, title?: string) => {
    setViewFileModal({
      open: true,
      filePath,
      title: title || 'View File',
    });
  };
  /**
   * File input change wrapper.
   */
  const handleFileChange = (bol_no: string, files: File[] | null) => {
    if (files && files.length > 0) {
      handleUploadFile(bol_no, files);
    }
  };

  /**
   * Open signed BOL / invoice file in new tab.
   */
  const handleDownloadInvoice = async (invoice_file: string | null) => {
    if (!invoice_file) return;

    fetch(`/uploads/${invoice_file}`)
      .then((response) => {
        if (!response.ok) {
          alert('File not found');
          throw new Error('File not found');
        }
        return response.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      })
      .catch((error) => {
        console.error('Error downloading file:', error);
      });
  };

  /**
   * Shared formatter for short shipper/receiver display.
   */
  const formatShortLocation = (party: any) =>
    `${party?.name || ''}${party?.postal_code ? ` (${party.postal_code.split(',')[0]})` : ''}`;

  /**
   * Shared formatter for BOL date.
   */
  const formatDate = (value?: string) =>
    value ? `${value.split('T')[0]} ${getDayOfWeek(value) ?? ''}` : '';

  /**
   * Vendor page table columns.
   */
  const columns = useMemo(
    () => [
      {
        key: 'shipper',
        label: 'Shipper',
        render: (row: any) => formatShortLocation(row.shipper),
      },
      {
        key: 'receiver',
        label: 'Receiver',
        render: (row: any) => formatShortLocation(row.receiver),
      },
      {
        key: 'po_no',
        label: 'PO No.',
        render: (row: any) =>
          Array.isArray(row.po) ? row.po.map((po: any) => po.po_no).join(', ') : '',
      },
      {
        key: 'bol_date',
        label: 'BOL Date',
        render: (row: any) => formatDate(row.bol_date),
      },
      {
        key: 'amount',
        label: 'Amount',
        render: (row: any) => `$${row?.amount ?? ''}`,
      },
      {
        key: 'type',
        label: 'Type',
      },
      {
        key: 'bol_no',
        label: 'BOL No',
        render: (row: any) => (
          <BolPrint bol={row} size="xs" variant="outline" textElement={row?.bol_no} />
        ),
      },
      {
        key: 'dc_received',
        label: 'DC Status',
        render: (row: any) => (
          <Chip
            size="small"
            label={row?.dc_received ? 'RECEIVED' : 'PENDING'}
            color={row?.dc_received ? 'info' : 'warning'}
            variant="outlined"
          />
        ),
      },
      {
        key: 'approval',
        label: 'Approval',
        render: (row: any) => (
          <Chip
            size="small"
            label={row?.approval ? 'APPROVED' : 'PENDING'}
            color={row?.approval ? 'success' : 'warning'}
            variant="outlined"
          />
        ),
      },
      {
        key: 'payment_status',
        label: 'Payment',
        render: (row: any) => (
          <Chip
            size="small"
            label={row?.payment_status ? 'PAID' : 'UNPAID'}
            color={row?.payment_status ? 'success' : 'error'}
            variant="outlined"
          />
        ),
      },
      {
        key: 'invoice',
        label: 'Invoice',
        render: (row: any) => {
          if (row?.invoice_no) {
            return <GenerateInvoice bol={row} textElement={row.invoice_no} />;
          }

          if (row?.vendor_delivered && row?.dc_received) {
            return <GenerateInvoice bol={row} textElement="Generate" />;
          }

          return (
            <Typography variant="body2" color="text.secondary">
              N/A
            </Typography>
          );
        },
      },
      {
        key: 'vendor_delivered',
        label: 'Delivered',
        render: (row: any) => (
          <Checkbox
            disabled={row.cancel ? true : row?.dc_received && row?.vendor_delivered}
            checked={!!row?.vendor_delivered}
            onChange={(event) => handleStatusChange(event.target.checked, row.bol_no)}
          />
        ),
      },
      {
        key: 'invoice_file',
        label: 'POD',
        render: (row: any) => (
          <Stack direction="row" spacing={1} alignItems="center">
            {row?.invoice_file ? (
              <Typography
                variant="body2"
                color="text.secondary"
              >
                <VisibilityIcon onClick={() =>
                  handleOpenFileModal(`/uploads/${row.invoice_file}`, 'Signed BOL')
                }
                  fontSize="small" color='success' sx={{ cursor: 'pointer', '&:hover': { color: 'white', backgroundColor: '#3a6e95' } }} />

              </Typography>
            ) : (
              <HoverCard
                // width={200}
                position="top"
                withinPortal={false}>
                <HoverCard.Target>
                  <Typography variant="body2" color="text.secondary">
                    <VisibilityOffIcon fontSize="small" />
                  </Typography>
                </HoverCard.Target>
                <HoverCard.Dropdown>
                  <Typography variant="body2" color="text.secondary">
                    No Signed BOL
                  </Typography>
                </HoverCard.Dropdown>
              </HoverCard>
            )}

            <input
              ref={(el) => {
                fileInputRefs.current[row.bol_no] = el;
              }}
              type="file"
              multiple
              style={{ display: 'none' }}
              disabled={row.cancel}
              onChange={(e) =>
                handleFileChange(
                  row.bol_no,
                  e.target.files ? Array.from(e.target.files) : null
                )
              }
            />

            <Button
              size="small"
              variant="outlined"
              disabled={row.cancel}
              startIcon={<UploadFileRoundedIcon />}
              onClick={() => fileInputRefs.current[row.bol_no]?.click()}
            >
              {row?.invoice_file ? 'Replace' : 'Upload'}
            </Button>
          </Stack>
        ),
      },
    ],
    []
  );

  /**
   * Optional extra row styling.
   */
  const getRowSx = (row: any) => {
    if (row.cancel) {
      return {
        backgroundColor: '#f5f5f5',
        '& td': {
          color: '#8c8c8c',
        },
      };
    }

    if (row.vendor_delivered && row.dc_received && row.payment_status) {
      return {
        backgroundColor: '#f8fff8',
      };
    }

    return {};
  };

  /**
   * Mobile card styling.
   */
  const getCardSx = (row: any) => {
    if (row.cancel) {
      return {
        backgroundColor: '#f5f5f5',
        '& *': {
          color: '#8c8c8c',
        },
      };
    }

    if (row.vendor_delivered && row.dc_received && row.payment_status) {
      return {
        backgroundColor: '#f8fff8',
      };
    }

    return {};
  };

  /**
   * Mobile / row actions.
   */
  const renderRowActions = (row: any) => (
    <Stack direction="row" spacing={1}>
      {row?.invoice_file ? (
        <Button
          size="small"
          variant="text"
          startIcon={<VisibilityIcon />}
          onClick={() => handleDownloadInvoice(row.invoice_file)}
        >
          View Signed BOL
        </Button>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No signed BOL
        </Typography>
      )}

      <input
        ref={(el) => {
          fileInputRefs.current[`mobile-${row.bol_no}`] = el;
        }}
        type="file"
        multiple
        style={{ display: 'none' }}
        disabled={row.cancel}
        onChange={(e) =>
          handleFileChange(
            row.bol_no,
            e.target.files ? Array.from(e.target.files) : null
          )
        }
      />

      <Button
        size="small"
        variant="outlined"
        disabled={row.cancel}
        startIcon={<UploadFileRoundedIcon />}
        onClick={() => fileInputRefs.current[`mobile-${row.bol_no}`]?.click()}
      >
        {/* <UploadFileRoundedIcon /> */}
        {/* {row?.invoice_file ? 'Replace Signed BOL' : 'Upload Signed BOL'} */}
      </Button>

      {/* <Button
        size="small"
        variant="outlined"
        onClick={() => handleStatusChange(!row.vendor_delivered, row.bol_no)}
        disabled={row.cancel ? true : row?.dc_received && row?.vendor_delivered}
      >
        {row?.vendor_delivered ? 'Delivered' : 'Mark Delivered'}
      </Button> */}
    </Stack>
  );

  return (
    <>
      <ViewFileModal
        open={viewFileModal.open}
        onClose={() =>
          setViewFileModal({
            open: false,
            filePath: null,
            title: 'View File',
          })
        }
        filePath={viewFileModal.filePath}
        title={viewFileModal.title}
      />
      <BolFilterModal
        open={filterModal}
        onClose={() => setFilterModal(false)}
        filters={filterDraft}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleResetAll}
      />

      <BolWorkspace
        data={bolList}
        columns={columns}
        pagination={pagination}
        setPagination={setPagination}
        search={search}
        setSearch={handleSearch}
        allowLocation={false}
        showFilterButton
        onOpenFilter={() => setFilterModal(true)}
        showCreateButton={false}
        // rowActions={renderRowActions}
        getRowSx={getRowSx}
        getCardSx={getCardSx}
        bottomRightSlot={<VendorReport storeList={storeList} />}
        showResetButton
        onResetFilters={handleResetAll}
      />
      <AppDialogMessage
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        variant={dialog.variant}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        hideCancel={dialog.hideCancel}
        onConfirm={dialog.onConfirm}
        onClose={closeDialog}
      />
    </>
  );
};

export default BolList;